import { apiClient } from './ApiClient'
import { User, PaginatedResponse, UserPreferences, UserSummary, CreateUserRequest, UpdateUserRequest, InvitationSummary } from '../types/entities'

export class UserService {
  private basePath = '/users'

  async getUsers(
    page = 1,
    pageSize = 20,
    role?: string,
    isActive?: boolean,
    organisationId?: string,
    childSiteId?: string
  ): Promise<PaginatedResponse<UserSummary>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    })

    if (role) params.append('role', role)
    if (isActive !== undefined) params.append('isActive', isActive.toString())
    if (organisationId) params.append('organisationId', organisationId)
    if (childSiteId) params.append('childSiteId', childSiteId)

    const users = await apiClient.get<UserSummary[]>(`${this.basePath}?${params}`)
    // Map response to paginated format
    return {
      data: users,
      totalCount: users.length,
      pageNumber: page,
      pageSize: pageSize,
      totalPages: Math.ceil(users.length / pageSize)
    }
  }

  async getUser(userId: string): Promise<User> {
    return apiClient.get<User>(`${this.basePath}/${userId}`)
  }

  async getCurrentUserProfile(): Promise<User> {
    return apiClient.get<User>(`${this.basePath}/profile`)
  }

  async updateUserProfile(profile: Partial<User>): Promise<void> {
    return apiClient.put<void>(`${this.basePath}/profile`, profile)
  }

  // Admin-only methods
  async createUser(request: CreateUserRequest): Promise<User> {
    return apiClient.post<User>(this.basePath, request)
  }

  async updateUser(userId: string, request: UpdateUserRequest): Promise<void> {
    return apiClient.put<void>(`${this.basePath}/${userId}`, request)
  }

  async deactivateUser(userId: string): Promise<void> {
    return apiClient.put<void>(`${this.basePath}/${userId}/deactivate`)
  }

  async activateUser(userId: string): Promise<void> {
    // Reactivate by updating isActive to true
    return this.updateUser(userId, { firstName: '', lastName: '', role: '', isActive: true })
  }

  // User Preferences
  async getMyPreferences(): Promise<UserPreferences> {
    return apiClient.get<UserPreferences>(`${this.basePath}/me/preferences`)
  }

  async updateMyPreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
    return apiClient.put<UserPreferences>(`${this.basePath}/me/preferences`, preferences)
  }

  // Invite user (uses auth endpoint)
  async inviteUser(request: {
    email: string
    firstName: string
    lastName: string
    role: string
    message?: string
    customerId?: string
    primaryOrganizationId?: string
    oversightChildSiteIds?: string[]
  }): Promise<void> {
    return apiClient.post<void>('/auth/invite', request)
  }

  // Get pending invitations
  async getPendingInvitations(): Promise<InvitationSummary[]> {
    return apiClient.get<InvitationSummary[]>(`${this.basePath}/invitations`)
  }

  // Resend invitation/verification email
  async resendInvitation(email: string): Promise<void> {
    return apiClient.post<void>('/auth/resend-verification', { email })
  }

  // Request password reset email
  async resetPassword(email: string): Promise<void> {
    return apiClient.post<void>('/auth/reset-password', { email })
  }

  // Delete user (Admin only)
  async deleteUser(userId: string): Promise<void> {
    return apiClient.delete<void>(`${this.basePath}/${userId}`)
  }
}

export const userService = new UserService()